/* oxlint-disable max-lines-per-function */
import { showExecEdEligibilityBundle } from 'AdmissionsGuidance/showExecEdEligibilityBundle';
import { useAngularContext } from 'AngularContext';
import { useNavigationHelper, useCurrentUser } from 'FrontRoyalAngular';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { type ProgramType, ProgramTypeConfigs, ProgramFamily, degreeProgramTypes } from 'Program';
import { getProgramFamilyApplication } from 'ProgramFamilyApplication';
import parseHtml from 'html-react-parser';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getCurrentlyPreventedFromSubmittingApplication,
    getHasPendingProgramApplication,
    userActionsApi,
    type BaseUser,
} from 'Users';
import { ClickAwayListener, Tooltip } from '@mui/material';
import useIsMobileMixin from 'StudentNetwork/react/useIsMobileMixin';
import { type ProgramListItem as ProgramListItemType } from '../selectProgram.types';

function useApplicationLogic(currentUser: BaseUser | null, programTypeForItem: ProgramType, isExecEdItem: boolean) {
    const programTypesInSameFamily = useMemo(
        () =>
            Object.keys(ProgramTypeConfigs).filter(
                programType =>
                    ProgramTypeConfigs[programType as ProgramType].programFamily ===
                    ProgramTypeConfigs[programTypeForItem].programFamily,
            ) as ProgramType[],
        [programTypeForItem],
    );

    const applicationPendingForThisProgramFamily = useMemo(() => {
        const execEdEligibilityBundle = currentUser?.execEdEligibilityBundle;
        const execEdProgramFamilyApplication = currentUser
            ? getProgramFamilyApplication(currentUser, ProgramFamily.exec_ed_certificate)
            : null;

        if (isExecEdItem && !!execEdProgramFamilyApplication && !execEdEligibilityBundle?.activatedAt) return true;

        if (isExecEdItem) return false;

        const hasPendingApplicationInFamily = programTypesInSameFamily.some(programType =>
            getHasPendingProgramApplication(currentUser, { programType }),
        );

        return hasPendingApplicationInFamily;
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentUser,
        currentUser?.programApplications, // an under-the-hood dependency
        programTypesInSameFamily,
        isExecEdItem,
    ]);

    const applicationPendingForAnyDegreeProgram = useMemo(
        () => degreeProgramTypes.some(programType => getHasPendingProgramApplication(currentUser, { programType })),
        [currentUser],
    );

    const preventedFromSubmitting = useMemo(() => {
        if (applicationPendingForThisProgramFamily) return true;

        // clicking Apply Now just opens the eligibility bundle modal, so don't prevent EE clicks unless pending.
        if (isExecEdItem) return false;

        const preventedFromSubmittingInFamily = programTypesInSameFamily.some(programType =>
            getCurrentlyPreventedFromSubmittingApplication(currentUser, { programType }),
        );

        return preventedFromSubmittingInFamily || applicationPendingForAnyDegreeProgram;
    }, [
        applicationPendingForThisProgramFamily,
        currentUser,
        programTypesInSameFamily,
        applicationPendingForAnyDegreeProgram,
        isExecEdItem,
    ]);

    return {
        applicationPendingForThisProgramFamily,
        applicationPendingForAnyDegreeProgram,
        preventedFromSubmitting,
    };
}

function useTooltipLogic({
    isExecEdItem,
    applicationPendingForAnyDegreeProgram,
    applicationPendingForThisProgramFamily,
    preapproved,
    preventedFromSubmitting,
}: {
    isExecEdItem: boolean;
    applicationPendingForAnyDegreeProgram: boolean;
    applicationPendingForThisProgramFamily: boolean;
    preapproved: boolean;
    preventedFromSubmitting: boolean;
}) {
    const { t } = useTranslation('back_royal');

    const tooltipMessage = useMemo(
        () =>
            !isExecEdItem && applicationPendingForAnyDegreeProgram && !applicationPendingForThisProgramFamily
                ? t('settings.select_program.pending_degree_application_message')
                : null,
        [isExecEdItem, applicationPendingForAnyDegreeProgram, applicationPendingForThisProgramFamily, t],
    );

    const ctaLocaleKey = useMemo(() => {
        if (preapproved) return 'settings.select_program.register';
        if (applicationPendingForThisProgramFamily) return 'settings.select_program.application_pending';
        if (preventedFromSubmitting) return 'settings.select_program.unavailable';
        return 'settings.select_program.apply_now';
    }, [preapproved, applicationPendingForThisProgramFamily, preventedFromSubmitting]);

    const ctaDisabled = !preapproved && (applicationPendingForThisProgramFamily || preventedFromSubmitting);

    return { tooltipMessage, ctaLocaleKey, ctaDisabled };
}

function createTooltipContents({
    tooltipMessage,
    ctaLocaleKey,
    ctaDisabled,
    handleApplyNowClick,
    t,
}: {
    tooltipMessage: string | null;
    ctaLocaleKey: string;
    ctaDisabled: boolean;
    handleApplyNowClick: () => Promise<void>;
    t: (key: string, params?: Record<string, unknown>) => string;
}) {
    return (
        <>
            {tooltipMessage && (
                <span className="border-0 bg-transparent p-0 outline-none">
                    {/* the yellow warning triangle emoji */}
                    ⚠️
                </span>
            )}
            <button
                type="button"
                onClick={handleApplyNowClick}
                data-testid="apply-now-button"
                disabled={ctaDisabled}
                className="apply-now inline-flex flex-wrap content-end justify-center rounded border-none bg-transparent p-1 text-xs text-coral disabled:cursor-not-allowed disabled:opacity-50"
            >
                {t(ctaLocaleKey).toLocaleUpperCase()}
            </button>
        </>
    );
}

function useTooltip({
    handleApplyNowClick,
    xsOrSm,
    preapproved,
    applicationPendingForThisProgramFamily,
    preventedFromSubmitting,
    isExecEdItem,
    applicationPendingForAnyDegreeProgram,
}: {
    handleApplyNowClick: () => Promise<void>;
    xsOrSm: boolean;
    preapproved: boolean;
    applicationPendingForThisProgramFamily: boolean;
    preventedFromSubmitting: boolean;
    isExecEdItem: boolean;
    applicationPendingForAnyDegreeProgram: boolean;
}) {
    const { t } = useTranslation('back_royal');
    const [mobileTooltipOpen, setMobileTooltipOpen] = useState(false);

    const { tooltipMessage, ctaLocaleKey, ctaDisabled } = useTooltipLogic({
        isExecEdItem,
        applicationPendingForAnyDegreeProgram,
        applicationPendingForThisProgramFamily,
        preapproved,
        preventedFromSubmitting,
    });

    const tooltipContents = createTooltipContents({
        tooltipMessage,
        ctaLocaleKey,
        ctaDisabled,
        handleApplyNowClick,
        t,
    });

    if (xsOrSm) {
        return (
            <ClickAwayListener onClickAway={() => setMobileTooltipOpen(false)}>
                <Tooltip
                    open={mobileTooltipOpen}
                    title={tooltipMessage ? parseHtml(tooltipMessage) : ''}
                    slotProps={{
                        popper: {
                            sx: {
                                '& .MuiTooltip-tooltip': {
                                    fontSize: '14px',
                                    fontFamily: '"ProximaNovaSoft", sans-serif',
                                    '& a': {
                                        color: '#fff',
                                        textDecoration: 'underline',
                                    },
                                },
                            },
                        },
                    }}
                >
                    <span
                        role="button"
                        data-testid="tooltip-trigger"
                        tabIndex={0}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                setMobileTooltipOpen(!mobileTooltipOpen);
                            }
                        }}
                        onClick={() => setMobileTooltipOpen(!mobileTooltipOpen)}
                    >
                        {tooltipContents}
                    </span>
                </Tooltip>
            </ClickAwayListener>
        );
    }

    return (
        <Tooltip
            title={tooltipMessage ? parseHtml(tooltipMessage) : ''}
            slotProps={{
                popper: {
                    sx: {
                        '& .MuiTooltip-tooltip': {
                            fontSize: '14px',
                            fontFamily: '"ProximaNovaSoft", sans-serif',
                            '& a': {
                                color: '#fff',
                                textDecoration: 'underline',
                            },
                        },
                    },
                },
            }}
        >
            <span data-testid="tooltip-trigger">{tooltipContents}</span>
        </Tooltip>
    );
}

function renderButtons(
    _item: ProgramListItemType,
    handleLearnMoreClick: () => void,
    loading: boolean,
    tooltipElement: React.ReactElement,
    t: (key: string, params?: Record<string, unknown>) => string,
) {
    return (
        <div className="buttons mt-auto flex justify-between p-4">
            <button
                type="button"
                data-testid="learn-more-button"
                onClick={handleLearnMoreClick}
                className="learn-more flex flex-wrap content-end justify-center rounded border-none bg-transparent p-1 text-xs text-blue"
            >
                {/* The string in other locales is longer and overlaps if nowrap is applied; styling nowrap only for 'en' */}
                <span className="lang-en:whitespace-nowrap">
                    {t('settings.select_program.learn_more').toLocaleUpperCase()}
                </span>
            </button>
            <div>
                {loading ? (
                    <div className="spinner-wrapper flex items-center justify-end">
                        <FrontRoyalSpinner className="no-top-margin no-delay" />
                    </div>
                ) : (
                    <span className="flex items-center justify-end">{tooltipElement}</span>
                )}
            </div>
        </div>
    );
}

function ProgramListItemComponent({ item, preapproved = false }: { item: ProgramListItemType; preapproved?: boolean }) {
    const { loadRoute, loadUrl } = useNavigationHelper();
    const $injector = useAngularContext();
    const currentUser = useCurrentUser();
    const [ensureAndChangePrefProgram] = userActionsApi.useEnsureAndChangePrefProgramMutation();
    const [offerAdmissionAlumniQuanticLaunch] = userActionsApi.useOfferAdmissionAlumniQuanticLaunchMutation();
    const [loading, setLoading] = useState<boolean>(false);
    const { xsOrSm } = useIsMobileMixin();
    const programTypeForItem = item.programType;
    const isExecEdItem = useMemo(() => ProgramTypeConfigs[programTypeForItem].isExecEd, [programTypeForItem]);

    const { applicationPendingForThisProgramFamily, applicationPendingForAnyDegreeProgram, preventedFromSubmitting } =
        useApplicationLogic(currentUser, programTypeForItem, isExecEdItem);

    const execEdEligibilityBundle = currentUser?.execEdEligibilityBundle;
    const execEdProgramFamilyApplication = useMemo(
        () => getProgramFamilyApplication(currentUser, ProgramFamily.exec_ed_certificate),
        [currentUser],
    );

    const handleApplyNowClick = useCallback(async () => {
        if (preapproved) {
            setLoading(true);
            await offerAdmissionAlumniQuanticLaunch({ program_type: programTypeForItem }).unwrap();
            setLoading(false);
            loadRoute('/settings/application_status');
            return;
        }

        if (!isExecEdItem) {
            // Is a degree program; change the user's program state pref and redirect to the application page
            setLoading(true);
            await ensureAndChangePrefProgram({ program_type: programTypeForItem }).unwrap();
            setLoading(false);
            loadRoute('/settings/application');
            return;
        }

        // Is an Exec Ed program

        if (execEdEligibilityBundle?.activatedAt) {
            // They have an activated bundle already; show the Congrats/Review and Register modal
            showExecEdEligibilityBundle($injector);
            return;
        }

        if (!execEdProgramFamilyApplication) {
            // They don't have an activated bundle and also no Exec Ed program family application;
            // redirect to the signup page
            const hostname = window.location.hostname;
            const isLocal =
                (hostname === 'localhost' || hostname === 'quantic.test' || hostname.endsWith('.quantic.test')) &&
                !window.CORDOVA;
            const isStaging = window.location.hostname.includes('staging');
            loadUrl(
                `${
                    isLocal
                        ? 'http://localhost:8000'
                        : isStaging
                          ? 'https://staging-quantic.com'
                          : 'https://quantic.edu'
                }/invite/exec-edu-signup`,
                '_blank',
            );
        }

        // Otherwise, they have a pending program family application; Apply Now button should
        // be disabled, but if they somehow click it, do nothing
    }, [
        loadRoute,
        loadUrl,
        ensureAndChangePrefProgram,
        offerAdmissionAlumniQuanticLaunch,
        isExecEdItem,
        programTypeForItem,
        $injector,
        execEdEligibilityBundle,
        execEdProgramFamilyApplication,
        preapproved,
    ]);

    const handleLearnMoreClick = useCallback(() => {
        loadUrl(item.learnMoreUrl, '_blank');
    }, [loadUrl, item]);

    return (
        <ProgramListItemPresentationComponent
            item={item}
            handleApplyNowClick={handleApplyNowClick}
            handleLearnMoreClick={handleLearnMoreClick}
            loading={loading}
            applicationPendingForThisProgramFamily={applicationPendingForThisProgramFamily}
            preventedFromSubmitting={preventedFromSubmitting}
            xsOrSm={xsOrSm}
            preapproved={preapproved}
            isExecEdItem={isExecEdItem}
            applicationPendingForAnyDegreeProgram={applicationPendingForAnyDegreeProgram}
        />
    );
}

function ProgramListItemPresentationComponent({
    item,
    handleApplyNowClick,
    handleLearnMoreClick,
    loading,
    applicationPendingForThisProgramFamily,
    preventedFromSubmitting,
    xsOrSm,
    preapproved = false,
    isExecEdItem,
    applicationPendingForAnyDegreeProgram,
}: {
    item: ProgramListItemType;
    handleApplyNowClick: () => Promise<void>;
    handleLearnMoreClick: () => void;
    loading: boolean;
    applicationPendingForThisProgramFamily: boolean;
    preventedFromSubmitting: boolean;
    xsOrSm: boolean;
    preapproved?: boolean;
    isExecEdItem: boolean;
    applicationPendingForAnyDegreeProgram: boolean;
}) {
    const { t } = useTranslation('back_royal');
    const itemDescriptionKey =
        preapproved && item.preapprovedDescription ? item.preapprovedDescription.key : item.description.key;

    return (
        <div
            key={item.title.key}
            className="program-list-item flex h-auto min-h-[300px] w-[290px] flex-col justify-start border border-solid border-[#f5f5f5] bg-white shadow-[0px_5px_5px_0px_rgba(0,0,0,.14)]"
        >
            {item.imageMode === 'center' && (
                <div className="image flex h-[157px] w-full items-center justify-center py-3">
                    <img className="h-full" src={item.image} alt={t(item.title.key, item.title.params ?? {})} />
                </div>
            )}
            {item.imageMode === 'cover' && (
                <div className="image h-[157px] w-full">
                    <img className="object-cover" src={item.image} alt={t(item.title.key, item.title.params ?? {})} />
                </div>
            )}
            <div className="content px-4 pb-8 pt-4">
                <h3 className="mb-4 mt-1.5 text-[18px] font-normal">{t(item.title.key, item.title.params ?? {})}</h3>
                <div className="mb-3 text-xs leading-normal text-grey-shuttle">
                    {t(itemDescriptionKey, item.description.params ?? {})}
                </div>
            </div>
            {renderButtons(
                item,
                handleLearnMoreClick,
                loading,
                useTooltip({
                    handleApplyNowClick,
                    xsOrSm,
                    preapproved,
                    applicationPendingForThisProgramFamily,
                    preventedFromSubmitting,
                    isExecEdItem,
                    applicationPendingForAnyDegreeProgram,
                }),
                t,
            )}
        </div>
    );
}

export const ProgramListItem = memo(ProgramListItemComponent) as typeof ProgramListItemComponent;
export const ProgramListItemPresentation = memo(
    ProgramListItemPresentationComponent,
) as typeof ProgramListItemPresentationComponent;
